import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getRanAwayReport, type RanAwayMentorRow, type RanAwayReport } from '../../lib/api';
import {
  C, num, inr, plural, pctOf, norm, fmtDay, fmtRange, monthName, todayIST, downloadCsv,
  presetPeriod, periodFromParams, periodParams, type Period, type ActiveFilter,
  ReportHeader, HeaderButton, PeriodPicker, SelectFilter, SearchFilter, ToggleChip, FilterCard, Section, Card, KpiTile, HowCounted,
  ColumnChart, MiniMeter, Pill, Notice, Loading, ServerOutdated, LinkButton,
} from '../../components/ReportKit';

// A mentor needs this many students before "highest rate" picks them — with
// fewer, one student moves the rate too much
const MIN_FOR_RATE = 5;
const PAGE = 25;

type Rank = 'count' | 'rate';
const RANK_SORT: Record<Rank, (a: RanAwayMentorRow, b: RanAwayMentorRow) => number> = {
  count: (a, b) => b.ranAway - a.ranAway || (b.rate ?? 0) - (a.rate ?? 0),
  rate:  (a, b) => (b.rate ?? 0) - (a.rate ?? 0) || b.ranAway - a.ranAway,
};

const roleLabel = (m: RanAwayMentorRow) => (!m.role || m.role === 'Standard' ? 'Mentor' : m.role);
const modeLabel = (m: string) => (m === 'online' ? 'Online' : m === 'offline' ? 'Offline' : '');

// ── main page ─────────────────────────────────────────────────────────────────
// Whose students run away. Filters: when students joined and course (fetched),
// then a mentor and a search for the list of students (here). The filters are
// kept in the page address.

const AdminRanAway: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const get = (k: string) => params.get(k) || '';

  const [period, setPeriod] = useState<Period>(() => periodFromParams(params, 'all'));
  const [course, setCourse] = useState(() => get('course'));
  const [mentor, setMentor] = useState(() => get('mentor'));
  const [rank, setRank]     = useState<Rank>(() => (get('rank') === 'rate' ? 'rate' : 'count'));
  const [search, setSearch] = useState(() => get('q'));
  const [showAll, setShowAll] = useState(false);

  const [data, setData]         = useState<RanAwayReport | null>(null);
  const [loading, setLoading]   = useState(true);
  const [outdated, setOutdated] = useState(false);
  const latest = useRef(0);

  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    try {
      const res = await getRanAwayReport(adminPassword, { from: period.from, to: period.to }, { course });
      if (call !== latest.current) return;
      setOutdated(false);
      setData(res);
    } catch (err: any) {
      if (call !== latest.current) return;
      if (/Unknown action/i.test(err.message)) setOutdated(true);
      else toast.error(err.message);
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [adminPassword, period.from, period.to, course]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams(periodParams(period));
    if (course) next.set('course', course);
    if (mentor) next.set('mentor', mentor);
    if (rank !== 'count') next.set('rank', rank);
    if (search) next.set('q', search);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [period, course, mentor, rank, search, params, setParams]);

  useEffect(() => { setShowAll(false); }, [mentor, search, data]);

  const mentors = useMemo(() => [...(data?.mentors ?? [])].sort((a, b) => RANK_SORT[rank](a, b) || a.name.localeCompare(b.name)), [data, rank]);
  const topCount = useMemo(() => [...(data?.mentors ?? [])].sort(RANK_SORT.count)[0], [data]);
  const topRate  = useMemo(() => [...(data?.mentors ?? [])].filter(m => m.students >= MIN_FOR_RATE).sort(RANK_SORT.rate)[0], [data]);
  const maxRan   = Math.max(1, ...mentors.map(m => m.ranAway));

  const students = useMemo(() => {
    const q = norm(search);
    return (data?.students ?? []).filter(s =>
      (!mentor || s.mentorId === mentor)
      && (!q || [s.name, s.studentId, s.phone, s.course, s.salesPerson].some(v => norm(v).includes(q))));
  }, [data, mentor, search]);
  const shown = showAll ? students : students.slice(0, PAGE);

  const t = data?.totals;
  const early = data?.earlyClasses ?? 5;
  const mentorName = data?.mentors.find(m => m.mentorId === mentor)?.name || mentor;
  const scope = [course || 'All courses', period.from ? `joined ${fmtRange(period.from, period.to)}` : 'joined any time'].join(' · ');

  const pickMentor = (id: string) => {
    setMentor(id);
    setTimeout(() => document.getElementById('ran-away-students')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const active: ActiveFilter[] = [
    ...(period.from ? [{ label: `Joined: ${fmtRange(period.from, period.to)}`, onRemove: () => setPeriod(presetPeriod('all')) }] : []),
    ...(course ? [{ label: `Course: ${course}`, onRemove: () => setCourse('') }] : []),
    ...(mentor ? [{ label: `Mentor: ${mentorName}`, onRemove: () => setMentor('') }] : []),
    ...(search ? [{ label: `Search: “${search}”`, onRemove: () => setSearch('') }] : []),
  ];
  const clearAll = () => { setPeriod(presetPeriod('all')); setCourse(''); setMentor(''); setSearch(''); };

  const openRevenue = () => navigate(`/tracker/admin/revenue?${new URLSearchParams({
    ...periodParams(period), ...(course ? { course } : {}), pay: 'ranaway', student: 'none',
  })}`);

  const exportCsv = () => downloadCsv(`ran-away-${todayIST()}.csv`,
    ['Student ID', 'Name', 'Phone', 'Course', 'Mode', 'Mentor', 'Joined', 'Classes attended', 'No-shows', 'Last attended', 'Course fee', 'Paid', 'Fees unpaid', 'Salesperson'],
    students.map(s => [s.studentId, s.name, s.phone, s.course, modeLabel(s.mode), s.mentorName, s.joined, s.attended, s.noShows,
      s.lastAttended, s.courseFee, s.paid, s.unpaid, s.salesPerson]));

  return (
    <TrackerLayout>
      <div className="space-y-6">
        <ReportHeader icon="🏃" title="Ran Away by Mentor"
          intro="Which mentors' students stop coming. For each mentor: the students given to them, how many ran away and how soon. Filter by when students joined to see recent batches only."
          actions={<>
            <HeaderButton onClick={exportCsv} disabled={!data || students.length === 0}>⬇ Export CSV</HeaderButton>
            <HeaderButton onClick={load}>↻ Refresh</HeaderButton>
          </>} />

        <FilterCard busy={loading && !!data} active={active} onClearAll={clearAll}
          showing={<>Showing <b className="text-gray-700">{t ? plural(t.students, 'student') : '…'}</b> given to a mentor · {scope}</>}>
          <PeriodPicker label="Joined" value={period} onChange={setPeriod} presets={['all', 'month', 'lastMonth', '90d', 'year', 'custom']} />
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter label="Course" value={course} onChange={setCourse} allLabel="All courses"
              options={(data?.options.courses ?? (course ? [course] : [])).map(c => ({ value: c, label: c }))} />
          </div>
        </FilterCard>

        {outdated && <ServerOutdated />}

        {!data || !t ? (loading ? <Loading text="Loading…" /> : null) : (
          <div className={`space-y-8 transition-opacity ${loading ? 'opacity-60' : ''}`}>

            {/* ── totals ── */}
            <Section title="Totals" caption={scope}>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KpiTile label="Students who ran away" value={num(t.ranAway)}
                  status={t.rate != null ? { tone: 'critical', label: `${t.rate}%` } : null}
                  hint={`Of the ${plural(t.students, 'student')} given to a mentor`} />
                <KpiTile label="Most ran away" value={topCount && topCount.ranAway > 0 ? topCount.name : '—'}
                  hint={topCount && topCount.ranAway > 0 ? `${num(topCount.ranAway)} of ${plural(topCount.students, 'student')} · ${topCount.rate}%` : 'Nobody ran away'}
                  onClick={topCount && topCount.ranAway > 0 ? () => pickMentor(topCount.mentorId) : undefined} cta="See who" />
                <KpiTile label="Highest rate" value={topRate && topRate.ranAway > 0 ? topRate.name : '—'}
                  hint={topRate && topRate.ranAway > 0
                    ? `${topRate.rate}% — ${num(topRate.ranAway)} of ${plural(topRate.students, 'student')}. Among mentors given ${MIN_FOR_RATE}+ students.`
                    : `No mentor with ${MIN_FOR_RATE}+ students had anyone run away`}
                  onClick={topRate && topRate.ranAway > 0 ? () => pickMentor(topRate.mentorId) : undefined} cta="See who" />
                <KpiTile label={`Left within ${early} classes`} value={num(t.early)}
                  hint={`${pctOf(t.early, t.ranAway) ?? 0}% of those who ran away — ${plural(t.never, 'student')} never attended a class`} />
                <KpiTile label="Fees unpaid" value={inr(t.unpaid)}
                  hint="Course fee − paid, for the students who ran away" />
              </div>
            </Section>

            {/* ── by mentor ── */}
            <Section title="By mentor" caption="A student counts for the mentor they're assigned to · click a mentor to list their students">
              <Card flush>
                <div className="flex flex-wrap items-center gap-1.5 px-5 pt-4 pb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Rank by</span>
                  <ToggleChip on={rank === 'count'} onClick={() => setRank('count')}>Most ran away</ToggleChip>
                  <ToggleChip on={rank === 'rate'} onClick={() => setRank('rate')}>Highest rate</ToggleChip>
                </div>
                {mentors.length === 0 ? (
                  <p className="text-center py-10 text-sm text-gray-400">No students match these filters</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          <th className="pl-5 pr-2 py-2.5 w-8">#</th>
                          <th className="px-4 py-2.5">Mentor</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Students given</th>
                          <th className="px-4 py-2.5 whitespace-nowrap">Ran away</th>
                          <th className="px-4 py-2.5 whitespace-nowrap">Rate</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Within {early} classes</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Fees unpaid</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Active <span className="normal-case font-normal text-gray-400">(now)</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {mentors.map((m, i) => {
                          const on = mentor === m.mentorId;
                          const above = t.rate != null && m.rate != null && m.ranAway > 0 && m.rate > t.rate;
                          return (
                            <tr key={m.mentorId} onClick={() => pickMentor(on ? '' : m.mentorId)}
                              className={`border-t border-gray-50 cursor-pointer ${on ? 'bg-purple-50/70' : 'hover:bg-gray-50'}`}
                              title={on ? 'Show every mentor’s students' : `List ${m.name}'s students who ran away`}>
                              <td className="pl-5 pr-2 py-3 text-xs font-bold text-gray-400 tabular-nums">{i + 1}</td>
                              <td className="px-4 py-3">
                                <p className="font-semibold text-gray-800">{m.name}</p>
                                <p className="text-[11px] text-gray-400">
                                  {roleLabel(m)}{m.status === 'Inactive' && ' · inactive'}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{num(m.students)}</td>
                              <td className="px-4 py-3">
                                <span className="flex items-center gap-2">
                                  <span className="h-2.5 rounded-sm flex-shrink-0" style={{ width: `${Math.max(m.ranAway ? 4 : 0, (m.ranAway / maxRan) * 96)}px`, background: C.critical }} />
                                  <span className="font-bold tabular-nums" style={{ color: m.ranAway ? C.ink : '#d1d5db' }}>{num(m.ranAway)}</span>
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <MiniMeter pct={m.rate} color={C.critical} />
                                  {above && <Pill tone="critical">Above overall</Pill>}
                                  {m.students < MIN_FOR_RATE && <span className="text-[10px] text-gray-400">only {plural(m.students, 'student')}</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{m.early ? num(m.early) : <span className="text-gray-300">0</span>}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{m.unpaid ? inr(m.unpaid) : <span className="text-gray-300">—</span>}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-gray-500">{num(m.active)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-100 bg-gray-50/60 text-xs">
                          <td className="pl-5 pr-2 py-2.5" />
                          <td className="px-4 py-2.5 font-bold text-gray-700">Everyone</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-700">{num(t.students)}</td>
                          <td className="px-4 py-2.5 tabular-nums font-bold text-gray-700">{num(t.ranAway)}</td>
                          <td className="px-4 py-2.5"><MiniMeter pct={t.rate} color={C.critical} /></td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-700">{num(t.early)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-700">{inr(t.unpaid)}</td>
                          <td className="px-4 py-2.5" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                {t.beforeMentor > 0 && (
                  <div className="px-5 py-3 border-t border-gray-100">
                    <Notice tone="neutral" action={<LinkButton onClick={openRevenue}>See them in Revenue →</LinkButton>}>
                      {plural(t.beforeMentor, 'more student')} ran away before being given a mentor{t.beforeMentorUnpaid > 0 && <> ({inr(t.beforeMentorUnpaid)} unpaid)</>} — not counted against anyone above.
                    </Notice>
                  </div>
                )}
              </Card>
            </Section>

            {/* ── by month joined ── */}
            <Section title="By month joined" caption={`Each column is the students who joined that month${course ? ` · ${course}` : ''}`}>
              <Card>
                <ColumnChart firstColumn="Month joined"
                  series={[{ name: 'Ran away', color: C.critical }, { name: 'Stayed', color: C.bar }]}
                  points={data.months.map(m => ({
                    key: m.key, label: m.label, title: `Joined in ${monthName(m.key)} · ${plural(m.joined, 'student')}`,
                    values: [m.ranAway, m.joined - m.ranAway],
                  }))}
                  note={p => { const pct = pctOf(p.values[0], p.values[0] + p.values[1]); return pct == null ? null : `${pct}% ran away`; }}
                  noteLabel="Ran away" emptyText="Nobody joined in these months" />
              </Card>
            </Section>

            {/* ── the students ── */}
            <Section id="ran-away-students" title="Students who ran away"
              caption={`${plural(students.length, 'student')}${mentor ? ` of ${mentorName}` : ''} · latest joiners first`}>
              <Card flush>
                <div className="flex flex-wrap items-end gap-3 px-5 pt-4 pb-3">
                  <SelectFilter label="Mentor" value={mentor} onChange={setMentor} allLabel="All mentors"
                    options={[...data.mentors].filter(m => m.ranAway > 0).sort((a, b) => a.name.localeCompare(b.name))
                      .map(m => ({ value: m.mentorId, label: `${m.name} (${m.ranAway})` }))} />
                  <SearchFilter label="Search" value={search} onChange={setSearch} placeholder="Name, ID, phone, course or salesperson…" />
                </div>
                {students.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm font-semibold text-gray-500">{t.ranAway === 0 ? 'Nobody ran away' : 'No students match these filters'}</p>
                    {(mentor || search) && <button onClick={() => { setMentor(''); setSearch(''); }} className="mt-2 text-xs font-bold underline text-gray-500">Show everyone</button>}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                            <th className="px-4 py-2.5">Student</th>
                            <th className="px-4 py-2.5">Mentor</th>
                            <th className="px-4 py-2.5">Course</th>
                            <th className="px-4 py-2.5">Joined</th>
                            <th className="px-4 py-2.5 whitespace-nowrap">Classes attended</th>
                            <th className="px-4 py-2.5 whitespace-nowrap">Last attended</th>
                            <th className="px-4 py-2.5 text-right whitespace-nowrap">Fees unpaid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shown.map(s => (
                            <tr key={s.studentId} className="border-t border-gray-50 align-top">
                              <td className="px-4 py-2.5">
                                <p className="font-semibold text-gray-800">{s.name}</p>
                                <p className="text-[11px] text-gray-400">{[s.studentId, s.phone].filter(Boolean).join(' · ')}</p>
                                {s.salesPerson && <p className="text-[11px] text-gray-400">Sales: {s.salesPerson}</p>}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-700">{s.mentorName}</td>
                              <td className="px-4 py-2.5 text-xs text-gray-600">
                                {s.course}{s.mode && <span className="block text-[11px] text-gray-400">{modeLabel(s.mode)}</span>}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDay(s.joined)}</td>
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <span className="font-bold tabular-nums text-gray-800">{num(s.attended)}</span>
                                {s.noShows > 0 && <span className="text-[11px] text-gray-400"> · {plural(s.noShows, 'no-show')}</span>}
                                {s.early && <span className="ml-1.5"><Pill tone="warning" icon={false}>{s.attended === 0 ? 'Never came' : 'Left early'}</Pill></span>}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{s.lastAttended ? fmtDay(s.lastAttended) : '—'}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                                {s.unpaid > 0 ? <span className="font-semibold text-gray-800">{inr(s.unpaid)}</span>
                                  : <span className="text-[11px] text-gray-400">{s.courseFee > 0 ? 'Paid in full' : 'Fee not set'}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {students.length > PAGE && (
                      <div className="px-5 py-3 border-t border-gray-100 text-center">
                        <button onClick={() => setShowAll(v => !v)} className="text-xs font-bold text-gray-600 hover:text-gray-900 underline underline-offset-2">
                          {showAll ? 'Show fewer' : `Show all ${num(students.length)}`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Section>

            <HowCounted items={[
              ['Ran away', "A student whose status is Ran Away on the Students page, or who was deactivated with their payment marked Ran Away on the Payments page. Active students and those on Institute Leave aren't counted, whatever their payment says."],
              ['Whose student', "A student counts for the mentor they're assigned to on the Students page — if they were moved, the mentor they were with last."],
              ['Students given', 'Every student assigned to the mentor (who joined in the period you pick) — active, finished, ran away or on leave.'],
              ['Rate', `Ran away ÷ students given. Above overall = higher than the rate for everyone shown. Highest rate only looks at mentors given ${MIN_FOR_RATE} or more students — with fewer, one student moves the rate a lot.`],
              [`Within ${early} classes`, `Ran away having attended ${early} classes or fewer, including students who never attended one.`],
              ['Classes attended', 'Classes logged with the student present, whichever mentor took them.'],
              ['Fees unpaid', "Course fee − paid, from the student's payment record. A student with no course fee set adds nothing."],
              ['Joined', 'The joining date on the Students page. The Joined filter picks students by it.'],
              ['Before a mentor', 'Payments marked Ran Away that match no student on the Students page — they left before being given a mentor.'],
            ]} />
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminRanAway;
